import { useState, useEffect, useCallback } from 'react'
import { feedbackService } from '../services/feedbackService'

const STATUS_OPTIONS = ['new', 'reviewed', 'resolved', 'dismissed']

const STATUS_COLORS = {
    new: 'bg-blue-100 text-blue-700 border-blue-200',
    reviewed: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    resolved: 'bg-green-100 text-green-700 border-green-200',
    dismissed: 'bg-gray-100 text-gray-500 border-gray-200',
}

const CATEGORY_COLORS = {
    'Bug Report': 'bg-red-50 text-red-700 border-red-200',
    'Feature Request': 'bg-purple-50 text-purple-700 border-purple-200',
    'General Feedback': 'bg-blue-50 text-blue-700 border-blue-200',
    'Incorrect Tender Match': 'bg-orange-50 text-orange-700 border-orange-200',
    'Incorrect Tender Classification': 'bg-amber-50 text-amber-700 border-amber-200',
    'Missing Tender': 'bg-rose-50 text-rose-700 border-rose-200',
}

const DEFAULT_CATEGORY_COLOR = 'bg-gray-50 text-gray-600 border-gray-200'

export default function Feedback() {
    const [feedback, setFeedback] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [toast, setToast] = useState(null)
    const [filterStatus, setFilterStatus] = useState('all')
    const [expandedId, setExpandedId] = useState(null)
    const [confirmDeleteId, setConfirmDeleteId] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 4000)
    }

    const fetchFeedback = useCallback(async () => {
        try {
            setError(null)
            const res = await feedbackService.getAll()
            const data = res?.data || res || []
            setFeedback(Array.isArray(data) ? data : [])
        } catch (err) {
            setError('Failed to load feedback.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchFeedback()
    }, [fetchFeedback])

    const handleStatusChange = async (id, status) => {
        try {
            await feedbackService.updateStatus(id, status)
            setFeedback(prev => prev.map(f => f._id === id ? { ...f, status } : f))
            showToast(`Status updated to "${status}"`)
        } catch {
            showToast('Failed to update status', 'error')
        }
    }

    const handleDelete = async (id) => {
        try {
            await feedbackService.remove(id)
            setFeedback(prev => prev.filter(f => f._id !== id))
            setConfirmDeleteId(null)
            showToast('Feedback deleted')
        } catch {
            showToast('Failed to delete', 'error')
        }
    }

    const filtered = filterStatus === 'all' ? feedback : feedback.filter(f => f.status === filterStatus)

    const counts = {
        all: feedback.length,
        new: feedback.filter(f => f.status === 'new').length,
        reviewed: feedback.filter(f => f.status === 'reviewed').length,
        resolved: feedback.filter(f => f.status === 'resolved').length,
        dismissed: feedback.filter(f => f.status === 'dismissed').length,
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-20 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fade-in ${
                    toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                }`}>
                    {toast.message}
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-admin-heading tracking-tight">User Feedback</h1>
                    <p className="text-sm text-admin-muted mt-1">Review and manage feedback submitted by platform users.</p>
                </div>
                <button
                    onClick={fetchFeedback}
                    className="px-4 py-2 text-sm font-semibold text-admin-text bg-white border border-admin-border rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {['all', ...STATUS_OPTIONS].map(status => (
                    <button
                        key={status}
                        onClick={() => setFilterStatus(status)}
                        className={`card-admin px-4 py-3 text-center transition-all ${
                            filterStatus === status ? 'ring-2 ring-accent-primary shadow-md' : 'hover:shadow-md'
                        }`}
                    >
                        <p className="text-2xl font-bold text-admin-heading">{counts[status] || 0}</p>
                        <p className="text-xs font-semibold text-admin-muted uppercase tracking-wider mt-1 capitalize">{status}</p>
                    </button>
                ))}
            </div>

            {/* Loading */}
            {loading && (
                <div className="card-admin divide-y divide-admin-border">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="p-5 animate-pulse flex items-center gap-4">
                            <div className="h-4 bg-gray-200 rounded w-24" />
                            <div className="h-4 bg-gray-100 rounded flex-1" />
                            <div className="h-4 bg-gray-200 rounded w-32" />
                        </div>
                    ))}
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="card-admin p-4 bg-red-50 border-red-200">
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                    <button onClick={fetchFeedback} className="text-sm text-red-700 underline font-semibold mt-1">Retry</button>
                </div>
            )}

            {/* Empty */}
            {!loading && !error && filtered.length === 0 && (
                <div className="card-admin py-16 text-center">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    <h3 className="text-lg font-bold text-admin-heading mb-1">No Feedback</h3>
                    <p className="text-sm text-admin-muted">
                        {filterStatus === 'all' ? 'No feedback has been submitted yet.' : `No feedback with status "${filterStatus}".`}
                    </p>
                </div>
            )}

            {/* Feedback table */}
            {!loading && !error && filtered.length > 0 && (
                <div className="card-admin overflow-hidden">
                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-admin-border">
                                    <th className="px-5 py-3 text-left text-[11px] font-bold text-admin-muted uppercase tracking-wider">User</th>
                                    <th className="px-5 py-3 text-left text-[11px] font-bold text-admin-muted uppercase tracking-wider">Category</th>
                                    <th className="px-5 py-3 text-left text-[11px] font-bold text-admin-muted uppercase tracking-wider">Message</th>
                                    <th className="px-5 py-3 text-left text-[11px] font-bold text-admin-muted uppercase tracking-wider">Status</th>
                                    <th className="px-5 py-3 text-left text-[11px] font-bold text-admin-muted uppercase tracking-wider">Date</th>
                                    <th className="px-5 py-3 text-right text-[11px] font-bold text-admin-muted uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-admin-border">
                                {filtered.map(item => (
                                    <TableRow
                                        key={item._id}
                                        item={item}
                                        expanded={expandedId === item._id}
                                        onToggle={() => setExpandedId(expandedId === item._id ? null : item._id)}
                                        onStatusChange={handleStatusChange}
                                        onDelete={handleDelete}
                                        confirmDeleteId={confirmDeleteId}
                                        setConfirmDeleteId={setConfirmDeleteId}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-admin-border">
                        {filtered.map(item => (
                            <MobileCard
                                key={item._id}
                                item={item}
                                expanded={expandedId === item._id}
                                onToggle={() => setExpandedId(expandedId === item._id ? null : item._id)}
                                onStatusChange={handleStatusChange}
                                onDelete={handleDelete}
                                confirmDeleteId={confirmDeleteId}
                                setConfirmDeleteId={setConfirmDeleteId}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

function TableRow({ item, expanded, onToggle, onStatusChange, onDelete, confirmDeleteId, setConfirmDeleteId }) {
    return (
        <>
            <tr className="hover:bg-gray-50/50 cursor-pointer transition-colors" onClick={onToggle}>
                <td className="px-5 py-3.5">
                    <p className="font-semibold text-admin-heading text-sm">{item.userName || 'Unknown'}</p>
                    <p className="text-[11px] text-admin-muted">{item.organisation || '—'}</p>
                </td>
                <td className="px-5 py-3.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${CATEGORY_COLORS[item.category] || DEFAULT_CATEGORY_COLOR}`}>
                        {item.category || 'General'}
                    </span>
                </td>
                <td className="px-5 py-3.5 max-w-xs">
                    <p className="text-sm text-admin-text truncate">{item.message}</p>
                </td>
                <td className="px-5 py-3.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border capitalize ${STATUS_COLORS[item.status] || STATUS_COLORS.new}`}>
                        {item.status || 'new'}
                    </span>
                </td>
                <td className="px-5 py-3.5 text-xs text-admin-muted whitespace-nowrap">
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </td>
                <td className="px-5 py-3.5 text-right">
                    <svg className={`w-4 h-4 inline-block text-admin-muted transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </td>
            </tr>

            {expanded && (
                <tr>
                    <td colSpan={6} className="bg-gray-50/80 px-5 py-4 border-t border-admin-border">
                        <ExpandedDetails
                            item={item}
                            onStatusChange={onStatusChange}
                            onDelete={onDelete}
                            confirmDeleteId={confirmDeleteId}
                            setConfirmDeleteId={setConfirmDeleteId}
                        />
                    </td>
                </tr>
            )}
        </>
    )
}

function MobileCard({ item, expanded, onToggle, onStatusChange, onDelete, confirmDeleteId, setConfirmDeleteId }) {
    return (
        <div className="p-4">
            <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={onToggle}>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${CATEGORY_COLORS[item.category] || DEFAULT_CATEGORY_COLOR}`}>
                            {item.category || 'General'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${STATUS_COLORS[item.status] || STATUS_COLORS.new}`}>
                            {item.status || 'new'}
                        </span>
                    </div>
                    <p className="text-sm font-semibold text-admin-heading">{item.userName || 'Unknown'}</p>
                    <p className="text-xs text-admin-muted line-clamp-2 mt-1">{item.message}</p>
                </div>
                <svg className={`w-4 h-4 text-admin-muted flex-shrink-0 transition-transform mt-1 ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {expanded && (
                <div className="mt-3 pt-3 border-t border-admin-border">
                    <ExpandedDetails
                        item={item}
                        onStatusChange={onStatusChange}
                        onDelete={onDelete}
                        confirmDeleteId={confirmDeleteId}
                        setConfirmDeleteId={setConfirmDeleteId}
                    />
                </div>
            )}
        </div>
    )
}

function ExpandedDetails({ item, onStatusChange, onDelete, confirmDeleteId, setConfirmDeleteId }) {
    const activeFilters = item.activeFilters && typeof item.activeFilters === 'object'
        ? Object.entries(item.activeFilters).filter(([, v]) => v !== null && v !== undefined)
        : []

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Detail label="Email" value={item.userEmail} />
                <Detail label="Organisation" value={item.organisation} />
                <Detail label="Submitted" value={item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Detail label="Page URL" value={item.pageUrl || item.page} link mono />
                {item.tenderId && <Detail label="Tender ID" value={item.tenderId} mono />}
            </div>

            <div>
                <p className="text-[11px] font-bold text-admin-muted uppercase tracking-wider mb-1.5">Full Message</p>
                <div className="bg-white rounded-lg border border-admin-border p-3 text-sm text-admin-text whitespace-pre-wrap">
                    {item.message}
                </div>
            </div>

            {activeFilters.length > 0 && (
                <div>
                    <p className="text-[11px] font-bold text-admin-muted uppercase tracking-wider mb-1.5">Active Filters at Submission</p>
                    <div className="bg-white rounded-lg border border-admin-border p-3 flex flex-wrap gap-2">
                        {activeFilters.map(([key, val]) => (
                            <span key={key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-xs text-admin-text">
                                <span className="font-semibold text-admin-muted">{key}:</span>
                                <span>{Array.isArray(val) ? val.join(', ') : String(val)}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-admin-border">
                <span className="text-xs font-bold text-admin-muted uppercase tracking-wider mr-1">Status:</span>
                {STATUS_OPTIONS.map(status => (
                    <button
                        key={status}
                        onClick={(e) => { e.stopPropagation(); onStatusChange(item._id, status) }}
                        disabled={item.status === status}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors capitalize ${
                            item.status === status
                                ? 'bg-accent-primary/10 border-accent-primary/30 text-accent-primary cursor-default'
                                : 'bg-white border-admin-border text-admin-text hover:bg-gray-50'
                        }`}
                    >
                        {status}
                    </button>
                ))}

                <div className="ml-auto">
                    {confirmDeleteId === item._id ? (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-red-600 font-semibold">Confirm?</span>
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(item._id) }}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
                            >
                                Delete
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null) }}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-admin-border text-admin-text hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item._id) }}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors"
                        >
                            Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

function Detail({ label, value, mono = false, link = false }) {
    return (
        <div>
            <p className="text-[11px] font-bold text-admin-muted uppercase tracking-wider">{label}</p>
            {link && value ? (
                <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm text-accent-primary hover:underline break-all mt-0.5 block ${mono ? 'font-mono text-xs' : ''}`}
                    onClick={e => e.stopPropagation()}
                >
                    {value}
                </a>
            ) : (
                <p className={`text-sm text-admin-text mt-0.5 break-all ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</p>
            )}
        </div>
    )
}
